import i18next from 'i18next';
import { toast } from 'sonner';
import { useServiceCatalogItemStore } from '../../store/service-catalog-item.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ServiceCatalogItemIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}) {
        const store = useServiceCatalogItemStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }
        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });
            const { data, pagination, uniqueValues } = response;
            store.setItems(data || []);
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                });
            }
            if (uniqueValues) store.setUniqueValues(uniqueValues);
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        const store = useServiceCatalogItemStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('itsmServiceCatalog:messages.createSuccess', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmServiceCatalog:messages.createFailed', { defaultValue: 'Could not create record' });
            toast.error(msg);
            return { success: false, error: msg, code: error?.code || null };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data) {
        const store = useServiceCatalogItemStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('itsmServiceCatalog:messages.updateSuccess', { defaultValue: 'Record updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmServiceCatalog:messages.updateFailed', { defaultValue: 'Could not update record' });
            toast.error(msg);
            return { success: false, error: msg, code: error?.code || null };
        } finally {
            store.setLoading(false);
        }
    }

    async delete(id) {
        const store = useServiceCatalogItemStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('itsmServiceCatalog:messages.deleteSuccess', { defaultValue: 'Record deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmServiceCatalog:messages.deleteFailed', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDelete(ids) {
        const store = useServiceCatalogItemStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        if (!tenantId) {
            store.setLoading(false);
            return {
                success: false,
                error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }),
            };
        }
        try {
            const response = await this.service.bulkDelete(ids);
            toast.success(
                i18next.t('itsmServiceCatalog:messages.bulkDeleteSuccess', {
                    count: ids.length,
                    defaultValue: '{{count}} records deleted',
                }),
            );
            return { success: true, data: response.data, deletedCount: response.deletedCount };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error)
                || i18next.t('itsmServiceCatalog:messages.bulkDeleteFailed', {
                    defaultValue: 'Could not delete selected records',
                });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchEditorHints() {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) return { success: false, data: { codes: [], correlationKeys: [] } };
        try {
            const response = await this.service.getEditorHints();
            return {
                success: true,
                data: response.data ?? { codes: [], correlationKeys: [] },
            };
        } catch (error) {
            return {
                success: false,
                data: { codes: [], correlationKeys: [] },
                error: getIntegrationErrorMessage(error),
            };
        }
    }

    async fetchPartyTemplate(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) return { success: false, data: null };
        try {
            const response = await this.service.getPartyTemplate(params);
            return { success: response.success !== false, data: response.data ?? null };
        } catch (error) {
            return { success: false, data: null, error: getIntegrationErrorMessage(error) };
        }
    }

    async fetchParty(id) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !id) return { success: false, data: null, partyTemplate: null };
        try {
            const response = await this.service.getParty(id);
            return {
                success: response.success !== false,
                data: response.data ?? null,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            return { success: false, data: null, partyTemplate: null, error: getIntegrationErrorMessage(error) };
        }
    }

    async syncParty(id, payload, options = {}) {
        const { silentSuccess = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !id) return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        try {
            const response = await this.service.syncParty(id, payload);
            if (!silentSuccess) {
                toast.success(i18next.t('itsmServiceCatalog:ownership.saveSuccess', {
                    defaultValue: 'Ownership and roles saved',
                }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('itsmServiceCatalog:ownership.saveFailed', {
                defaultValue: 'Ownership could not be saved',
            });
            toast.error(msg);
            return { success: false, error: msg };
        }
    }
}
